// src/components/GroupBudgetDashboard.tsx
import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Tabs, Tab, Chip, Button, 
  FormControl, InputLabel, Select, MenuItem, Grid, 
  Collapse, IconButton
} from '@mui/material';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Cell, PieChart, Pie 
} from 'recharts';
import { 
  Download, ExpandMore, ExpandLess, ArrowUpward, ArrowDownward, 
  KeyboardArrowDown, KeyboardArrowUp 
} from '@mui/icons-material';
import { GroupBudgetData } from '../types/groupBudget';
import { 
  IMC_INCOME_CODES, 
  IMC_EXPENSE_CODES,
  getIMCCodeByCode
} from '../constants/imcCodes';
import { getFrequencyMultiplier } from '../utils/frequencyUtils';
import axiosInstance from '../utils/axiosConfig';
import BudgetOverviewPanel from './BudgetOverviewPanel';


interface CodeBreakdown {
  budgetName: string;
  value: number;
  quantity: number;
  multiplier: number;
  frequency?: string;
}
interface ActualData {
  code: string;
  description: string;
  actual: number;
}

interface ChartDataItem {
  name: string;
  budgetValue: number;
  actualValue?: number; // Add this line

}

interface DataItem {
  code: string;
  name: string;
  description: string;
  budgetValue: number;
  category: string;
  IconComponent: React.ElementType;
  frequency?: string;
  breakdown: CodeBreakdown[];
  actualValue?: number; // Add this line

}

type ChartDataAccumulator = Record<string, ChartDataItem>;

interface GroupBudgetDashboardProps {
  budget: GroupBudgetData;
}

const GroupBudgetDashboard: React.FC<GroupBudgetDashboardProps> = ({ budget }) => {
  const [tabValue, setTabValue] = useState(0);
  const [showAllIncome, setShowAllIncome] = useState(false);
  const [showAllExpense, setShowAllExpense] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [sortColumn, setSortColumn] = useState<'code' | 'budgetValue' | 'actualValue' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expandedIncomeRows, setExpandedIncomeRows] = useState<string[]>([]);
  const [expandedExpenseRows, setExpandedExpenseRows] = useState<string[]>([]);
  const [actualsData, setActualsData] = useState<ActualData[]>([]);
  const [loadingActuals, setLoadingActuals] = useState<boolean>(false);
  const [errorActuals, setErrorActuals] = useState<string | null>(null);

  useEffect(() => {
    const fetchActualsData = async () => {
      setLoadingActuals(true);
      setErrorActuals(null);
      
      try {
        const response = await axiosInstance.get<ActualData[]>('/api/sheet-data');
        setActualsData(response.data);
        console.log('Loaded actuals data:', response.data);
      } catch (error) {
        console.error('Error fetching actuals data:', error);
        setErrorActuals('Failed to load actuals data. Please try again later.');
      } finally {
        setLoadingActuals(false);
      }
    };
  
    fetchActualsData();
  }, []);

  useEffect(() => {
    // Only run if we actually have data
    if (!budget?.expenseLines || actualsData.length === 0) return;
  
    console.log('--- Debugging Expense Lines vs. Actuals ---');
    budget.expenseLines.forEach(line => {
      const foundActual = actualsData.find(item => item.code === line.code);
      console.log(
        'Line code:', line.code,
        'Found actual code in actualsData?', foundActual ? 'YES' : 'NO',
        'Value:', foundActual?.actual
      );
    });
  }, [budget, actualsData]);
  
  // Add helper function to get actual value
  const getActualValueByCode = (code: string): number => {
    const actualItem = actualsData.find(item => item.code === code);
    return actualItem?.actual || 0;
  };

  // Toggle row expansion (Income)
  const toggleIncomeRowExpansion = (code: string) => {
    setExpandedIncomeRows((prev) =>
      prev.includes(code)
        ? prev.filter((c) => c !== code)
        : [...prev, code]
    );
  };

  // Toggle row expansion (Expense)
  const toggleExpenseRowExpansion = (code: string) => {
    setExpandedExpenseRows((prev) =>
      prev.includes(code)
        ? prev.filter((c) => c !== code)
        : [...prev, code]
    );
  };

  const calculateSummary = () => {
    // Sum all lines' totalValue (regardless of whether quantity × value = 0).
    const totalBudgetIncome =
      budget?.incomeLines?.reduce(
        (sum, line) => sum + (line.totalValue || 0),
        0
      ) || 0;
  
    // Use .reduce on all expenseLines, ignoring zero checks:
    const totalBudgetExpense =
      budget?.expenseLines?.reduce(
        (sum, line) => sum + (line.totalValue || 0),
        0
      ) || 0;
  
    // Sum actual for all income lines
    const totalActualIncome =
      budget?.incomeLines?.reduce(
        (sum, line) => sum + getActualValueByCode(line.code),
        0
      ) || 0;
  
    // Sum actual for all expense lines, even if budgetValue is 0
    const totalActualExpense =
      budget?.expenseLines?.reduce(
        (sum, line) => sum + getActualValueByCode(line.code),
        0
      ) || 0;
  
    return {
      totalBudgetIncome,
      totalActualIncome,
      totalBudgetExpense,
      totalActualExpense,
    };
  };

  const { totalBudgetIncome, totalActualIncome, totalBudgetExpense, totalActualExpense } = calculateSummary();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Get all income data
  const getIncomeData = () => {
    const allIncomeCodes = Object.values(IMC_INCOME_CODES);
    const displayCodes = showAllIncome
      ? allIncomeCodes
      : allIncomeCodes.filter((code) =>
          budget.incomeLines.some(
            (line) =>
              line.code === code.code &&
              (line.totalValue || line.total || 0) > 0
          )
        );

    return displayCodes
      .map((codeInfo) => {
        const budgetLine = budget.incomeLines.find(
          (line) => line.code === codeInfo.code
        );
        const budgetValue = budgetLine?.totalValue || 0;
        const breakdown = budgetLine?.breakdown || [];

        return {
          code: codeInfo.code,
          name: codeInfo.label.replace(/^\d+ - /, ''),
          description: codeInfo.description,
          budgetValue,
          category: codeInfo.subCategories[0],
          actualValue: getActualValueByCode(codeInfo.code),
          IconComponent: codeInfo.icon,
          frequency: budgetLine?.frequency || '',
          breakdown,
        };
      })
      .sort((a, b) => Number(a.code) - Number(b.code));
  };

  // Get all expense data
  const getExpenseData = () => {
    const allExpenseCodes = Object.values(IMC_EXPENSE_CODES);

    // If showAllExpense is true, show all codes.
    // Otherwise, only show codes that actually appear in budget.expenseLines,
    // but do NOT filter out lines that have zero quantity or value.
    const displayCodes = showAllExpense
      ? allExpenseCodes
      : allExpenseCodes.filter((code) =>
          budget.expenseLines.some(
            (line) => line.code === code.code
          )
        );

    return displayCodes
      .map((codeInfo) => {
        // Find the matching expense line (if any) in the budget
        const budgetLine = budget.expenseLines.find(
          (line) => line.code === codeInfo.code
        );
        
        // Fallback to 0 if not found
        const quantity = budgetLine?.quantity || 0;
        const value = budgetLine?.value || 0;
        const multiplier = budgetLine?.multiplier || 1;
        const frequency = budgetLine?.frequency || '';
        const freqMult = getFrequencyMultiplier(frequency);

        // Calculate the budget value (even if 0)
        const budgetValue = quantity * value * multiplier * freqMult;
        const breakdown = budgetLine?.breakdown || [];

        return {
          code: codeInfo.code,
          name: codeInfo.label.replace(/^\d+ - /, ''),
          description: codeInfo.description,
          budgetValue,
          category: codeInfo.subCategories[0],
          IconComponent: codeInfo.icon,
          actualValue: getActualValueByCode(codeInfo.code),
          frequency,
          breakdown,
        };
      })
      .sort((a, b) => Number(a.code) - Number(b.code));
  };

  const toggleSort = (column: 'code' | 'budgetValue' | 'actualValue') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedIncomeData = React.useMemo(() => {
    const data = getIncomeData();
    if (!sortColumn) return data;
    if (sortColumn === 'code') {
      return data.sort((a, b) =>
        sortDirection === 'asc'
          ? Number(a.code) - Number(b.code)
          : Number(b.code) - Number(a.code)
      );
    }
    if (sortColumn === 'budgetValue') {
      return data.sort((a, b) =>
        sortDirection === 'asc'
          ? a.budgetValue - b.budgetValue
          : b.budgetValue - a.budgetValue
      );
    }
    return data;
  }, [sortColumn, sortDirection, showAllIncome, budget]);

  const sortedExpenseData = React.useMemo(() => {
    const data = getExpenseData();
    if (!sortColumn) return data;
    if (sortColumn === 'code') {
      return data.sort((a, b) =>
        sortDirection === 'asc'
          ? Number(a.code) - Number(b.code)
          : Number(b.code) - Number(a.code)
      );
    }
    if (sortColumn === 'budgetValue') {
      return data.sort((a, b) =>
        sortDirection === 'asc'
          ? a.budgetValue - b.budgetValue
          : b.budgetValue - a.budgetValue
      );
    }
    return data;
  }, [sortColumn, sortDirection, showAllExpense, budget]);

  // Get chart data grouped by subcategory
  const getChartData = (category: 'income' | 'expense') => {
    const data: DataItem[] =
      category === 'income' ? getIncomeData() : getExpenseData();

    const groupedData = data.reduce<ChartDataAccumulator>((acc, item) => {
      if (!item.category) return acc;
      if (!acc[item.category]) {
        acc[item.category] = {
          name:
            item.category.charAt(0).toUpperCase() +
            item.category.slice(1),
          budgetValue: 0,
        };
      }
      acc[item.category].budgetValue += item.budgetValue;
      return acc;
    }, {} as ChartDataAccumulator);

    return Object.values(groupedData).sort(
      (a, b) => b.budgetValue - a.budgetValue
    );
  };

  // Prepare comparison data (not heavily used in example, but kept for reference)
  const getBudgetComparisonData = () => {
    const comparisonData: {
      name: string;
      code: string;
      budget: number;
      category: 'income' | 'expense';
      subCategory: string;
    }[] = [];

    // Income comparison
    for (const line of budget.incomeLines) {
      const code = line.code;
      const codeInfo = getIMCCodeByCode(code, IMC_INCOME_CODES);
      if (codeInfo) {
        comparisonData.push({
          name: codeInfo.label.replace(/^\d+ - /, ''),
          code: code,
          budget:
            (line.quantity || 0) *
            (line.value || 0) *
            (line.multiplier || 1),
          category: 'income',
          subCategory: codeInfo.subCategories[0],
        });
      }
    }

    // Expense comparison
    for (const line of budget.expenseLines) {
      const code = line.code;
      const codeInfo = getIMCCodeByCode(code, IMC_EXPENSE_CODES);
      if (codeInfo) {
        comparisonData.push({
          name: codeInfo.label.replace(/^\d+ - /, ''),
          code: code,
          budget:
            (line.quantity || 0) *
            (line.value || 0) *
            (line.multiplier || 1),
          category: 'expense',
          subCategory: codeInfo.subCategories[0],
        });
      }
    }

    return comparisonData;
  };

  return (
    <Box sx={{ width: '100%', bgcolor: 'background.default', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            {budget.name} Dashboard
          </Typography>
          <Box>
            <FormControl sx={{ minWidth: 120, mr: 2 }}>
              <InputLabel id="year-select-label">Year</InputLabel>
              <Select
                labelId="year-select-label"
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value as number)}
                size="small"
              >
                <MenuItem value={2025}>2025</MenuItem>
                <MenuItem value={2024}>2024</MenuItem>
                <MenuItem value={2023}>2023</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" startIcon={<Download />} size="small">
              Export
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={`Type: ${budget.budgetType}`}
            color="primary"
            size="small"
            variant="outlined"
          />
          <Chip
            label={`Events: ${budget.eventCount}`}
            color="primary"
            size="small"
            variant="outlined"
          />
          <Chip
            label={`Status: ${budget.status}`}
            color={budget.status.includes('Final') ? 'success' : 'warning'}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`VAT: ${budget.vatStatus}`}
            color="primary"
            size="small"
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 5 }}>
      <Paper sx={{ p: 2, flexGrow: 1, minWidth: '180px' }}>
  <Typography variant="body2" color='#39FF14'>
    Total Budget Income
  </Typography>
  <Typography variant="h5" sx={{ mt: 1 }}>
    €
    {totalBudgetIncome.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}
  </Typography>
</Paper>
+
<Paper sx={{ p: 2, flexGrow: 1, minWidth: '180px' }}>
  <Typography variant="body2" color='#39FF14'>
    Arts Council Grant
  </Typography>
  <Typography variant="h5" sx={{ mt: 1 }}>
    €
350,000.00

  </Typography>
</Paper>
=
<Paper sx={{ p: 2, flexGrow: 1, minWidth: '180px' }}>
  <Typography variant="body2" color='#39FF14'>
    Combined Income
  </Typography>
  <Typography variant="h5" sx={{ mt: 1 }}>
    €
    {(totalBudgetIncome + 350000).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}
  </Typography>
</Paper>

        <Paper sx={{ p: 2, flexGrow: 1, minWidth: '180px' }}>
  <Typography variant="body2" color='#FF3131'>
    Total Budget Expenses
  </Typography>
  <Typography variant="h5" sx={{ mt: 1 }}>
    €
    {totalBudgetExpense.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}
  </Typography>
</Paper>

        <Paper sx={{ p: 2, flexGrow: 1, minWidth: '180px' }}>
  <Typography variant="body2" color="text.secondary">
    Net Position (Budget)
  </Typography>
  <Typography variant="h5" sx={{ mt: 1 }}>
    €
    {(
      totalBudgetIncome - totalBudgetExpense + 350000
    ).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}
  </Typography> 
</Paper>
      </Box>

      {/* Tabs for different views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="P&L View" />
          <Tab label="Charts & Analytics" />
          <Tab label="Audience & Employment" />
        </Tabs>
      </Box>

      {/* P&L View */}
      {tabValue === 0 && (
        <Box>
              <BudgetOverviewPanel
      expenseLines={budget.expenseLines}
      getActualValueByCode={getActualValueByCode}
    />
  {/* Income Table */}
<Paper sx={{ mb: 4 }}>
  <TableContainer>
    <Table aria-label="income table">
    <TableHead>
  {/* Single merged cell spanning all columns */}
  <TableRow sx={{ bgcolor: 'primary.main' }}>
    <TableCell colSpan={6} sx={{ color: 'white' }}>
      <Typography variant="h6">INCOME</Typography>
    </TableCell>
  </TableRow>

  {/* Actual columns below */}
  <TableRow sx={{ bgcolor: 'primary.light' }}>
    {/* 1) Expand/collapse arrow cell */}
    <TableCell sx={{ width: '40px' }} />

    {/* 2) Code cell with onClick sort */}
    <TableCell
      sx={{
        color: 'white',
        cursor: 'pointer',
        whiteSpace: 'nowrap', 
        alignItems: 'center',
      }}
      onClick={() => toggleSort('code')}
    >
      Code
      {sortColumn === 'code' &&
        (sortDirection === 'asc' ? (
          <ArrowUpward fontSize="small" />
        ) : (
          <ArrowDownward fontSize="small" />
        ))}
    </TableCell>

    {/* 3) Description cell */}
    <TableCell sx={{ color: 'white' }}>
      Description
    </TableCell>

    {/* 4) Budget cell with onClick sort */}
    <TableCell
      align="right"
      sx={{
        color: 'white',
        cursor: 'pointer',
        whiteSpace: 'nowrap', 
        alignItems: 'center',
      }}
      onClick={() => toggleSort('budgetValue')}
    >
      Budget {selectedYear}
      {sortColumn === 'budgetValue' &&
        (sortDirection === 'asc' ? (
          <ArrowUpward fontSize="small" />
        ) : (
          <ArrowDownward fontSize="small" />
        ))}
    </TableCell>

    {/* 5) Actual cell with onClick sort */}
    <TableCell
      align="right"
      sx={{
        color: 'white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
      onClick={() => toggleSort('actualValue')}
    >
      Actual {selectedYear}
      {sortColumn === 'actualValue' &&
        (sortDirection === 'asc' ? (
          <ArrowUpward fontSize="small" />
        ) : (
          <ArrowDownward fontSize="small" />
        ))}
    </TableCell>

    {/* 6) Variance cell */}
    <TableCell align="right" sx={{ color: 'white' }}>
      Variance/Bal
    </TableCell>
  </TableRow>
</TableHead>
      <TableBody>
        {sortedIncomeData.map((row) => (
          <React.Fragment key={row.code}>
            <TableRow
              sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                cursor:
                  row.breakdown?.length > 0 ? 'pointer' : 'default',
                backgroundColor: expandedIncomeRows.includes(
                  row.code
                )
                  ? 'rgba(0, 0, 0, 0.04)'
                  : 'inherit',
              }}
              onClick={() =>
                row.breakdown?.length > 0 &&
                toggleIncomeRowExpansion(row.code)
              }
            >
              <TableCell>
                {row.breakdown?.length > 0 && (
                  <IconButton size="small" aria-label="expand row">
                    {expandedIncomeRows.includes(row.code) ? (
                      <KeyboardArrowUp />
                    ) : (
                      <KeyboardArrowDown />
                    )}
                  </IconButton>
                )}
              </TableCell>
              <TableCell component="th" scope="row">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ mr: 1, color: 'primary.main' }}>
                    <row.IconComponent fontSize="small" />
                  </Box>
                  {row.code}
                </Box>
              </TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell align="right">
                {row.frequency &&
                ['Monthly', 'Quarterly', 'Bi-Monthly'].includes(
                  row.frequency
                ) ? (
                  <Box sx={{ position: 'relative' }}>
                    <Typography>
                      €
                      {(
                        row.budgetValue /
                        getFrequencyMultiplier(row.frequency)
                      ).toLocaleString()}
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{ ml: 0.5, color: 'text.secondary' }}
                      >
                        /
                        {row.frequency
                          .toLowerCase()
                          .replace('ly', '')}
                      </Typography>
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      (€{row.budgetValue.toLocaleString()}/year)
                    </Typography>
                  </Box>
                ) : (
                  <>€{row.budgetValue.toLocaleString()}</>
                )}
              </TableCell>
              <TableCell align="right">
                €{(row.actualValue || 0).toLocaleString()}
              </TableCell>
              <TableCell 
                align="right"
                sx={{ 
                  color: ((row.actualValue || 0) - row.budgetValue) < 0 
                    ? 'error.main' 
                    : 'success.main' 
                }}
              >
                €{((row.actualValue || 0) - row.budgetValue).toLocaleString()}
              </TableCell>
            </TableRow>

            {row.breakdown?.length > 0 && (
              <TableRow>
                <TableCell
                  style={{ paddingBottom: 0, paddingTop: 0 }}
                  colSpan={6}
                >
                  <Collapse
                    in={expandedIncomeRows.includes(row.code)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box sx={{ margin: 1 }}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        component="div"
                      >
                        Budget Breakdown
                      </Typography>
                      <Table
                        size="small"
                        aria-label={`${row.code} breakdown`}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>Budget</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">
                              Quantity
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {row.breakdown.map((item, index) => (
                            <TableRow
                              key={`${row.code}-${index}`}
                            >
                              <TableCell
                                component="th"
                                scope="row"
                              >
                                {item.budgetName}
                              </TableCell>
                              <TableCell align="right">
                                {item.frequency &&
                                ['Monthly', 'Quarterly', 'Bi-Monthly'].includes(
                                  item.frequency
                                ) ? (
                                  <Box
                                    sx={{ position: 'relative' }}
                                  >
                                    <Typography>
                                      €
                                      {(
                                        item.value /
                                        getFrequencyMultiplier(
                                          item.frequency
                                        )
                                      ).toLocaleString()}
                                      <Typography
                                        variant="caption"
                                        component="span"
                                        sx={{
                                          ml: 0.5,
                                          color: 'text.secondary',
                                        }}
                                      >
                                        /
                                        {item.frequency
                                          .toLowerCase()
                                          .replace('ly', '')}
                                      </Typography>
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      (€
                                      {item.value.toLocaleString()}
                                      /year)
                                    </Typography>
                                  </Box>
                                ) : (
                                  <>€
                                    {item.value.toLocaleString()}
                                  </>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {item.quantity}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        ))}

        <TableRow>
          <TableCell colSpan={6} align="center">
            <Button
              size="small"
              onClick={() => setShowAllIncome(!showAllIncome)}
              endIcon={showAllIncome ? <ExpandLess /> : <ExpandMore />}
            >
              {showAllIncome
                ? 'Show Non-Zero Items'
                : 'Show All Income Items'}
            </Button>
          </TableCell>
        </TableRow>

        <TableRow sx={{ bgcolor: 'primary.light' }}>
          <TableCell></TableCell>
          <TableCell colSpan={2} sx={{ color: 'white' }}>
            <Typography variant="subtitle1">TOTAL INCOME</Typography>
          </TableCell>
          <TableCell
            align="right"
            sx={{ color: 'white', fontWeight: 'bold' }}
          >
            €{totalBudgetIncome.toLocaleString()}
          </TableCell>
          <TableCell
            align="right"
            sx={{ color: 'white', fontWeight: 'bold' }}
          >
            €{totalActualIncome.toLocaleString()}
          </TableCell>
          <TableCell
            align="right"
            sx={{ 
              color: 'white', 
              fontWeight: 'bold',
              bgcolor: totalActualIncome - totalBudgetIncome < 0 ? 'error.dark' : 'success.dark'
            }}
          >
            €{(totalActualIncome - totalBudgetIncome).toLocaleString()}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
</Paper>

 {/* Expense Table */}
<Paper sx={{ mb: 4 }}>
  <TableContainer>
    <Table aria-label="expense table">
      <TableHead>
        <TableRow sx={{ bgcolor: 'error.main' }}>
          <TableCell colSpan={6} sx={{ color: 'white' }}>
            <Typography variant="h6">EXPENDITURE</Typography>
          </TableCell>
        </TableRow>
        <TableRow sx={{ bgcolor: 'error.light' }}>
          <TableCell sx={{ width: '40px' }}></TableCell>
          <TableCell
            sx={{
              color: 'white',
              cursor: 'pointer',
              whiteSpace: 'nowrap', 
              alignItems: 'center',
            }}
            onClick={() => toggleSort('code')}
          >
            Code
            {sortColumn === 'code' &&
              (sortDirection === 'asc' ? (
                <ArrowUpward fontSize="small" />
              ) : (
                <ArrowDownward fontSize="small" />
              ))}
          </TableCell>
          <TableCell sx={{ color: 'white' }}>
            Description
          </TableCell>
          <TableCell
            align="right"
            sx={{
              color: 'white',
              cursor: 'pointer',
              whiteSpace: 'nowrap', 
              alignItems: 'center',
            }}
            onClick={() => toggleSort('budgetValue')}
          >
            Budget {selectedYear}
            {sortColumn === 'budgetValue' &&
              (sortDirection === 'asc' ? (
                <ArrowUpward fontSize="small" />
              ) : (
                <ArrowDownward fontSize="small" />
              ))}
          </TableCell>
          <TableCell
            align="right"
            sx={{
              color: 'white',
              cursor: 'pointer',
              whiteSpace: 'nowrap', 
              alignItems: 'center',
            }}
            onClick={() => toggleSort('actualValue')}
          >
            Actual {selectedYear}
            {sortColumn === 'actualValue' &&
              (sortDirection === 'asc' ? (
                <ArrowUpward fontSize="small" />
              ) : (
                <ArrowDownward fontSize="small" />
              ))}
          </TableCell>
          <TableCell
            align="right"
            sx={{ color: 'white' }}
          >
            Variance/Bal
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedExpenseData.map((row) => (
          <React.Fragment key={row.code}>
            <TableRow
              sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                cursor:
                  row.breakdown?.length > 0 ? 'pointer' : 'default',
                backgroundColor: expandedExpenseRows.includes(
                  row.code
                )
                  ? 'rgba(0, 0, 0, 0.04)'
                  : 'inherit',
              }}
              onClick={() =>
                row.breakdown?.length > 0 &&
                toggleExpenseRowExpansion(row.code)
              }
            >
              <TableCell>
                {row.breakdown?.length > 0 && (
                  <IconButton size="small" aria-label="expand row">
                    {expandedExpenseRows.includes(row.code) ? (
                      <KeyboardArrowUp />
                    ) : (
                      <KeyboardArrowDown />
                    )}
                  </IconButton>
                )}
              </TableCell>
              <TableCell component="th" scope="row">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ mr: 1, color: 'error.main' }}>
                    <row.IconComponent fontSize="small" />
                  </Box>
                  {row.code}
                </Box>
              </TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell align="right">
                €{row.budgetValue.toLocaleString()}
              </TableCell>
              <TableCell align="right">
                €{(row.actualValue || 0).toLocaleString()}
              </TableCell>
              <TableCell 
                align="right"
                sx={{ 
                  color: ((row.actualValue || 0) - row.budgetValue) > 0 
                    ? 'error.main'  // For expenses, over budget is bad
                    : 'success.main' 
                }}
              >
                €{((row.actualValue || 0) - row.budgetValue).toLocaleString()}
              </TableCell>
            </TableRow>

            {row.breakdown?.length > 0 && (
              <TableRow>
                <TableCell
                  style={{ paddingBottom: 0, paddingTop: 0 }}
                  colSpan={6}
                >
                  <Collapse
                    in={expandedExpenseRows.includes(row.code)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box sx={{ margin: 1 }}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        component="div"
                      >
                        Budget Breakdown
                      </Typography>
                      <Table
                        size="small"
                        aria-label={`${row.code} breakdown`}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>Budget</TableCell>
                            <TableCell align="right">
                              Amount
                            </TableCell>
                            <TableCell align="right">
                              Quantity
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {row.breakdown.map((item, index) => (
                            <TableRow
                              key={`${row.code}-${index}`}
                            >
                              <TableCell
                                component="th"
                                scope="row"
                              >
                                {item.budgetName}
                              </TableCell>
                              <TableCell align="right">
                                {item.frequency &&
                                ['Monthly', 'Quarterly', 'Bi-Monthly'].includes(
                                  item.frequency
                                ) ? (
                                  <Box
                                    sx={{ position: 'relative' }}
                                  >
                                    <Typography>
                                      €
                                      {(
                                        item.value /
                                        getFrequencyMultiplier(
                                          item.frequency
                                        )
                                      ).toLocaleString()}
                                      <Typography
                                        variant="caption"
                                        component="span"
                                        sx={{
                                          ml: 0.5,
                                          color: 'text.secondary',
                                        }}
                                      >
                                        /
                                        {item.frequency
                                          .toLowerCase()
                                          .replace('ly', '')}
                                      </Typography>
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      (€
                                      {item.value.toLocaleString()}
                                      /year)
                                    </Typography>
                                  </Box>
                                ) : (
                                  <>€
                                    {item.value.toLocaleString()}
                                  </>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {item.quantity}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        ))}

        <TableRow>
          <TableCell colSpan={6} align="center">
            <Button
              size="small"
              onClick={() => setShowAllExpense(!showAllExpense)}
              endIcon={showAllExpense ? <ExpandLess /> : <ExpandMore />}
            >
              {showAllExpense
                ? 'Show Non-Zero Items'
                : 'Show All Expense Items'}
            </Button>
          </TableCell>
        </TableRow>

        <TableRow sx={{ bgcolor: 'error.light' }}>
          <TableCell></TableCell>
          <TableCell colSpan={2} sx={{ color: 'white' }}>
            <Typography variant="subtitle1">
              TOTAL EXPENDITURE
            </Typography>
          </TableCell>
          <TableCell
            align="right"
            sx={{ color: 'white', fontWeight: 'bold' }}
          >
            €{totalBudgetExpense.toLocaleString()}
          </TableCell>
          <TableCell
            align="right"
            sx={{ color: 'white', fontWeight: 'bold' }}
          >
            €{totalActualExpense.toLocaleString()}
          </TableCell>
          <TableCell
            align="right"
            sx={{ 
              color: 'white', 
              fontWeight: 'bold',
              bgcolor: totalActualExpense - totalBudgetExpense > 0 ? 'error.dark' : 'success.dark'
            }}
          >
            €{(totalActualExpense - totalBudgetExpense).toLocaleString()}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
</Paper>
        </Box>
      )}

      {/* Charts & Analytics View */}
      {tabValue === 1 && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Income by Category
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={getChartData('income')}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) =>
                        `€${Number(value).toLocaleString()}`
                      }
                      labelFormatter={(label) => `Category: ${label}`}
                    />
                    <Legend />
                    <Bar dataKey="budgetValue" name="Budget" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Expense by Category
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={getChartData('expense')}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) =>
                        `€${Number(value).toLocaleString()}`
                      }
                      labelFormatter={(label) => `Category: ${label}`}
                    />
                    <Legend />
                    <Bar dataKey="budgetValue" name="Budget" fill="#ff8042" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Budget Overview
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[
                      { name: 'Income', budget: totalBudgetIncome },
                      { name: 'Expenses', budget: totalBudgetExpense },
                      {
                        name: 'Net Position',
                        budget: totalBudgetIncome - totalBudgetExpense,
                      },
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) =>
                        `€${Number(value).toLocaleString()}`
                      }
                    />
                    <Legend />
                    <Bar dataKey="budget" name="Budget" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Top Income Sources
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={getIncomeData()
                        .slice(0, 5)
                        .map((item) => ({
                          name: item.name,
                          value: item.budgetValue,
                        }))}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {getIncomeData()
                        .slice(0, 5)
                        .map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={`#${(index * 5 + 3) * 111111}`}
                          />
                        ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) =>
                        `€${Number(value).toLocaleString()}`
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Audience & Employment View */}
      {tabValue === 2 && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Audience Metrics
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Venue Capacity</TableCell>
                        <TableCell align="right">
                          {budget.venueCapacity?.toLocaleString() || 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Paying Attendance</TableCell>
                        <TableCell align="right">
                          {budget.payingAttendance?.toLocaleString() || 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Free Attendance</TableCell>
                        <TableCell align="right">
                          {budget.freeAttendance?.toLocaleString() || 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Total Attendance</TableCell>
                        <TableCell align="right">
                          {budget.totalAttendance?.toLocaleString() || 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Attendance Ratio</TableCell>
                        <TableCell align="right">
                          {budget.attendanceRatio?.toFixed(1) || 'N/A'}%
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Attendance Breakdown
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: 'Paying',
                            value: budget.payingAttendance || 0,
                          },
                          { name: 'Free', value: budget.freeAttendance || 0 },
                          {
                            name: 'Unfilled',
                            value:
                              (budget.venueCapacity || 0) -
                              (budget.totalAttendance || 0),
                          },
                        ]}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        <Cell fill="#8884d8" />
                        <Cell fill="#82ca9d" />
                        <Cell fill="#d0d0d0" />
                      </Pie>
                      <Tooltip
                        formatter={(value) => Number(value).toLocaleString()}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Employment Data
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Artist Employment</TableCell>
                        <TableCell align="right">
                          {budget.artistEmployment || 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Other Employment</TableCell>
                        <TableCell align="right">
                          {budget.otherEmployment || 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Associated Employment</TableCell>
                        <TableCell align="right">
                          {budget.associatedEmployment || 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Staff Employment</TableCell>
                        <TableCell align="right">
                          {budget.staffCount || 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Total Employment</TableCell>
                        <TableCell align="right">
                          {budget.totalEmployment || 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Volunteers</TableCell>
                        <TableCell align="right">
                          {budget.volunteers || 'N/A'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Employment Breakdown
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: 'Artists',
                            value: budget.artistEmployment || 0,
                          },
                          {
                            name: 'Associated',
                            value: budget.associatedEmployment || 0,
                          },
                          {
                            name: 'Other',
                            value: budget.otherEmployment || 0,
                          },
                          { name: 'Staff', value: budget.staffCount || 0 },
                        ]}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        <Cell fill="#8884d8" />
                        <Cell fill="#82ca9d" />
                        <Cell fill="#ffc658" />
                        <Cell fill="#ff8042" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Additional Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Event Type
                      </Typography>
                      <Typography variant="body1">
                        {budget.eventType}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Target Audience
                      </Typography>
                      <Typography variant="body1">
                        {budget.targetAudience}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Event Count
                      </Typography>
                      <Typography variant="body1">
                        {budget.eventCount}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Location
                      </Typography>
                      <Typography variant="body1">
                        {budget.location || 'Not specified'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Geographic Focus
                      </Typography>
                      <Typography variant="body1">
                        {budget.geoFocus || 'Not specified'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Period
                      </Typography>
                      <Typography variant="body1">
                        {budget.period || 'Not specified'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default GroupBudgetDashboard;